import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { z } from 'zod';

import { queryClient } from '~/api/queryClient';
import { queryKeys } from '~/api/queryKeys';
import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { CostGatePanel } from '~/features/catalog-enrichment/components/cost-gate-panel';
import { useRunProgress } from '~/features/catalog-enrichment/hooks/use-run-progress';
import { getRunProgress } from '~/features/catalog-enrichment/services/run.service';
import { gateRedirectSearch } from '~/features/catalog-enrichment/utils/run-decisions';
import {
  runProductsSearchSchema,
  type RunProductsSearch,
} from '~/features/catalog-enrichment/utils/run-products-search';

const RunProductsPage = lazy(() => import('~/features/catalog-enrichment/pages/run-products-page'));

/** The Review tab's search: the products list's own params, plus the old `/gate?productId=` key.
 *  (`stage` is the list's own filter here; an old `?stage=<gate>` falls back to the open gate.) */
const gateSearchSchema = runProductsSearchSchema.extend({
  productId: z.string().optional().catch(undefined),
});

/**
 * The batch's Review tab (design guide part 2 §2.5): where the decision the batch waits on is
 * made — a cost approval's panel, then the open gate's decision panel above the products that
 * need a look. Everything on screen is in the URL. An old `?productId=` link (a Slack/email
 * "review" link) is expanded into the review lens in `beforeLoad` — a visible redirect, never an
 * effect that rewrites the URL after the page rendered.
 */
export const Route = createFileRoute('/_authenticated/_tenant/catalog-enrichment/runs/$runId/gate')(
  {
    validateSearch: gateSearchSchema,
    beforeLoad: async ({ params, search }) => {
      if (!search.productId) return;
      const { productId, ...rest } = search;
      // The seed depends on the open gate (only Content generation opens a product in focus).
      let openStage: string | null = null;
      try {
        const progress = await queryClient.ensureQueryData({
          queryKey: queryKeys.catalogService.runProgress(params.runId),
          queryFn: () => getRunProgress(params.runId),
        });
        openStage = progress.pendingGate?.stage ?? null;
      } catch {
        // Unknown gate: the redirect still drops the old key; the decision panel shows the open one.
      }
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router's redirect
      throw redirect({
        to: '/catalog-enrichment/runs/$runId/gate',
        params: { runId: params.runId },
        search: { ...rest, ...gateRedirectSearch(null, productId, openStage) },
        replace: true,
      });
    },
    component: RouteComponent,
  },
);

/** True while the browser is on a batch's Review route. */
function isReviewPath(pathname: string): boolean {
  return pathname.replace(/\/$/, '').endsWith('/gate');
}

function RouteComponent() {
  const { runId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const router = useRouter();
  const progress = useRunProgress(runId);
  // A search-only navigate is bound to this route (`from: …/gate`): fired after the user left
  // for another tab (this component is still mounted mid-transition, reading the new location's
  // search), it would pull them straight back to Review. Only ever update while still here.
  const stillHere = () => isReviewPath(router.state.location.pathname);
  // Decisions, filters, pages and opened sheets push history entries (Back undoes them);
  // search keystrokes pass `replace`.
  const onSearchChange = (patch: Partial<RunProductsSearch>, opts?: { replace?: boolean }) => {
    if (!stillHere()) return;
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: opts?.replace ?? false });
  };

  if (progress.isLoading) return <PageSkeleton />;
  const costGate = progress.data?.costGate ?? null;

  return (
    <div className="flex flex-col gap-4" data-testid="catalog-enrichment-run-review-page">
      {costGate ? <CostGatePanel runId={runId} waiting={costGate} /> : null}
      {costGate && !progress.data?.pendingGate ? null : (
        <Suspense fallback={<PageSkeleton />}>
          <RunProductsPage
            key={runId}
            runId={runId}
            search={search}
            onSearchChange={onSearchChange}
          />
        </Suspense>
      )}
    </div>
  );
}
